import { useEffect, useMemo, useRef, useState } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Modal, Pagination, Spin } from "antd";
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Legend,
  Tooltip,
} from "chart.js";
import { Doughnut, Bar } from "react-chartjs-2";
import {
  DashboardMember,
  MemberStage,
  DashboardSummary,
} from "../../../../mock/teamDashboard";
import {
  getTeamDetailOverview,
  getTeamDetailCharts,
  getTeamDetailTrackingTable
} from "../funtion";
import {
  TeamDetailOverviewApi,
  TeamDetailChartDataApi,
  TeamDetailTrackingTable,
  TreemapSeries,
  TmItem,
  TmRect
} from "../type";
import { CheckSquareOffset, CalendarBlank } from '@phosphor-icons/react';
import "../style/dashboard.scss";

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

// How many notifications to show at the top of the page.
const TOP_NOTIF_LIMIT = 6;



function hexToRgb(hex: string) {
  const h = hex.replace("#", "");
  return { r: parseInt(h.substr(0, 2), 16), g: parseInt(h.substr(2, 2), 16), b: parseInt(h.substr(4, 2), 16) };
}
function mixColor(c1: string, c2: string, t: number) {
  const a = hexToRgb(c1);
  const b = hexToRgb(c2);
  const r = Math.round(a.r + (b.r - a.r) * t);
  const g = Math.round(a.g + (b.g - a.g) * t);
  const bch = Math.round(a.b + (b.b - a.b) * t);
  return `rgb(${r},${g},${bch})`;
}
function pnlToColor(pnl: number) {
  const t = Math.max(-1, Math.min(1, pnl / 8));
  if (t >= 0) return mixColor("#E3F2E6", "#1B7A37", t);
  return mixColor("#FBE4E4", "#C0392B", -t);
}
function textColorFor(rgbStr: string) {
  const m = (rgbStr.match(/\d+/g) || []).map(Number);
  const lum = (0.299 * m[0] + 0.587 * m[1] + 0.114 * m[2]) / 255;
  return lum > 0.62 ? "#1A1814" : "#FFFFFF";
}


function squarify(items: TmItem[], x: number, y: number, w: number, h: number): TmRect[] {
  if (!items.length) return [];
  if (items.length === 1) return [{ ...items[0], rx: x, ry: y, rw: w, rh: h }];

  const total = items.reduce((s, d) => s + d.value, 0);
  const shortSide = Math.min(w, h);

  let bestSplit = 1;
  let bestWorst = Infinity;
  let runningSum = 0;
  for (let i = 0; i < items.length; i++) {
    runningSum += items[i].value;
    const rowArea = (runningSum / total) * w * h;
    const rowSide = rowArea / shortSide;
    let worst = 0;
    for (let j = 0; j <= i; j++) {
      const itemArea = (items[j].value / total) * w * h;
      const otherSide = itemArea / rowSide;
      const ratio = Math.max(rowSide / otherSide, otherSide / rowSide);
      worst = Math.max(worst, ratio);
    }
    if (worst <= bestWorst) {
      bestWorst = worst;
      bestSplit = i + 1;
    } else break;
  }

  const row = items.slice(0, bestSplit);
  const rest = items.slice(bestSplit);
  const rowSum = row.reduce((s, d) => s + d.value, 0);
  const rowArea = (rowSum / total) * w * h;

  let result: TmRect[] = [];
  if (w >= h) {
    const colW = rowArea / h;
    let cy = y;
    row.forEach((item) => {
      const itemH = ((item.value / total) * w * h) / colW;
      result.push({ ...item, rx: x, ry: cy, rw: colW, rh: itemH });
      cy += itemH;
    });
    result = result.concat(squarify(rest, x + colW, y, w - colW, h));
  } else {
    const rowH = rowArea / w;
    let cx = x;
    row.forEach((item) => {
      const itemW = ((item.value / total) * w * h) / rowH;
      result.push({ ...item, rx: cx, ry: y, rw: itemW, rh: rowH });
      cx += itemW;
    });
    result = result.concat(squarify(rest, x, y + rowH, w, h - rowH));
  }
  return result;
}

// Member table helpers
const STAGE_MAP: Record<MemberStage, { c: string; bg: string; label: string }> = {
  funded: { c: "#2D7A3A", bg: "#E8F5EB", label: "Funded" },
  challenge: { c: "#1A5FA8", bg: "#E8F1FC", label: "Challenge" },
  verification: { c: "#5B3FA8", bg: "#EEE8FC", label: "Verification" },
  new: { c: "#9B9589", bg: "#F0EDE6", label: "Mới" },
};

type TableFilter = "all" | "funded" | "challenge" | "verification" | "danger";

// Overview cards double as filters for the member tracker.
type MetricFilter = "all" | "growing" | "danger" | "funded" | "pass" | "losses" | "noSl" | "noJournal";

// Prefer the backend health status; fall back to the drawdown rule when it is missing.
// (The old `sl >= 5` rule flagged almost every member, since SL counts are cumulative.)
function isDangerMember(m: DashboardMember): boolean {
  if (m.healthStatus) return healthLevel(m.healthStatus) === "urgent";
  return m.dd >= 7;
}

// --- CÁC HÀM HELPER ĐỊNH DẠNG CHO TOOLTIP ---
const getInitials = (name: string) => {
  if (!name) return "";
  const parts = name.split(" ");
  return parts.length > 1
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : parts[0][0].toUpperCase();
};

const fmtBalance = (val?: number | null) => {
  if (val === undefined || val === null || isNaN(val)) return "0 USD";
  return `${val.toLocaleString()} USD`;
};

const fmtPnl = (pnl: number) => {
  return `${Math.abs(pnl)}%`;
};

const pnlArrow = (pnl: number) => {
  if (pnl > 0) return "▲ ";
  if (pnl < 0) return "▼ ";
  return "";
};

// Đổi vị trí tooltip nếu ô nằm quá sát rìa trái/phải (ở đây tạm để center, bạn có thể custom thêm)
const getTooltipPositionClass = (index: number) => {
  return "left-1/2 -translate-x-1/2";
};

const TOOLTIP_WIDTH = 220;
const TOOLTIP_HEIGHT = 190; // Chiều cao ước tính của hộp tooltip + mũi tên

const calculateTooltipStyle = (r: any, layoutW: number, layoutH: number) => {
  // 1. Tìm điểm chính giữa của ô thỏi theo trục X
  let left = r.rx + r.rw / 2 - TOOLTIP_WIDTH / 2;

  // Giới hạn biên Trái / Phải để không lọt ra ngoài khung chart
  if (left < 5) left = 5;
  if (left + TOOLTIP_WIDTH > layoutW - 5) left = layoutW - TOOLTIP_WIDTH - 5;

  // 2. Mặc định hiển thị phía trên ô (Cách ô một khoảng 10px)
  let top = r.ry - TOOLTIP_HEIGHT;
  let isBottomMode = false;

  // Nếu ô nằm quá sát mép TRÊN, không đủ chỗ hiện hộp tooltip -> Đẩy nó xuống HIỂN THỊ PHÍA DƯỚI ô
  if (top < 5) {
    top = r.ry + r.rh + 10;
    isBottomMode = true;
  }

  return {
    style: {
      position: "absolute" as const,
      left: `${left}px`,
      top: `${top}px`,
      width: `${TOOLTIP_WIDTH}px`,
      zIndex: 999,
    },
    isBottomMode,
    // Tính vị trí mũi tên tam giác lệch theo ô đang hover
    arrowLeft: r.rx + r.rw / 2 - left
  };
};

interface TreemapProps {
  data: TreemapSeries[]; // Thay thế bằng type chuẩn của bạn: TreemapSeries[]
  onSelect?: (accountNumber: string) => void;
}

// Health status coming from the tracking table ("Nguy hiểm", "Cần chú ý", ...) mapped to a notification level.
function healthLevel(status?: string): "urgent" | "warning" | null {
  const h = (status || "").toLowerCase();
  if (h.includes("nguy hiểm") || h.includes("danger") || h.includes("red")) return "urgent";
  if (h.includes("cần chú ý") || h.includes("attention") || h.includes("amber") || h.includes("warning")) return "warning";
  return null;
}

// Phones get horizontal bar charts labelled with member names: vertical bars with rotated account numbers
// overlap and are unreadable at ~375px.
const NARROW_QUERY = "(max-width: 640px)";
function useIsNarrow(): boolean {
  const [narrow, setNarrow] = useState(() => typeof window !== "undefined" && window.matchMedia(NARROW_QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia(NARROW_QUERY);
    const onChange = () => setNarrow(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return narrow;
}
const shortName = (s: string, max = 16) => (s.length > max ? `${s.slice(0, max - 1)}…` : s);
const hBarHeight = (rows: number) => Math.max(160, rows * 28 + 40);

type PerfView = "map" | "list";
type PerfSortKey = "pnl" | "balance" | "drawdown";
const PERF_VIEW_KEY = "team-perf-view";

const readPerfView = (): PerfView => {
  try {
    return localStorage.getItem(PERF_VIEW_KEY) === "list" ? "list" : "map";
  } catch {
    return "map";
  }
};

const fmtK = (val: number) => `${Number((val / 1000).toFixed(2))}k USD`;

// List alternative to the treemap: same data, but sortable and searchable so a leader can pick a member quickly.
const PerformanceList: React.FC<TreemapProps> = ({ data, onSelect }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<{ key: PerfSortKey; dir: "asc" | "desc" }>({ key: "pnl", dir: "desc" });

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const value = (s: TreemapSeries) =>
      sort.key === "pnl" ? s.pnl_percent : sort.key === "balance" ? s.account_balance : s.drawdown || 0;
    return data
      .filter((s) => !q || `${s.first_name} ${s.last_name}`.toLowerCase().includes(q) || s.account_number.includes(q))
      .sort((a, b) => (sort.dir === "asc" ? 1 : -1) * (value(a) - value(b)));
  }, [data, query, sort]);

  // Same saturation point as the treemap colours (±8%), so one blown account doesn't flatten every other bar.
  const PNL_BAR_CAP = 8;

  const toggleSort = (key: PerfSortKey) =>
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === "desc" ? "asc" : "desc" } : { key, dir: "desc" }));

  const SORTS: { key: PerfSortKey; label: string }[] = [
    { key: "pnl", label: t("PnlMonth") },
    { key: "balance", label: t("Balance") },
    { key: "drawdown", label: t("Teams_dashboard.Drawdown") },
  ];

  return (
    <div className="tdv2-perf-list">
      <div className="tdv2-perf-toolbar">
        <input
          type="search"
          className="tdv2-perf-search"
          placeholder={t("SearchMemberOrAccount")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="tdv2-perf-sorts">
          {SORTS.map((s) => (
            <button key={s.key} type="button" className={sort.key === s.key ? "active" : ""} onClick={() => toggleSort(s.key)}>
              {s.label}
              {sort.key === s.key && <span className="dir">{sort.dir === "desc" ? "↓" : "↑"}</span>}
            </button>
          ))}
        </div>
      </div>
      <div className="tdv2-perf-rows">
        {rows.length === 0 ? (
          <div className="tdv2-perf-empty">{t("NoMatchingMembers")}</div>
        ) : (
          rows.map((s, i) => {
            const name = `${s.first_name} ${s.last_name}`.trim();
            const pos = s.pnl_percent >= 0;
            const width = (Math.min(Math.abs(s.pnl_percent), PNL_BAR_CAP) / PNL_BAR_CAP) * 50;
            const dd = s.drawdown || 0;
            return (
              <div
                key={s.account_number}
                className={`tdv2-perf-row${onSelect ? " clickable" : ""}${s.is_danger ? " danger" : ""}`}
                onClick={() => onSelect?.(s.account_number)}
              >
                <span className="rank">{i + 1}</span>
                <div className="who">
                  <div className="nm">{name}</div>
                  <div className="acc">#{s.account_number} · {fmtK(s.account_size)}</div>
                </div>
                <div className="bar" aria-hidden>
                  <div className={`fill ${pos ? "pos" : "neg"}`} style={{ width: `${width}%` }} />
                </div>
                <div className="vals">
                  <span className={`pnl ${pos ? "pos" : "neg"}`}>{pos ? "+" : ""}{s.pnl_percent}%</span>
                  <span className="bal">
                    {fmtBalance(s.account_balance)}
                    <span className={`dd${dd > 3 ? " hi" : ""}`}><span className="sep"> · </span>DD {dd.toFixed(2)}%</span>
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

const Treemap: React.FC<TreemapProps> = ({ data, onSelect }) => {
  const wrapRef = useRef<HTMLDivElement>(null);

  const [layout, setLayout] = useState<{ rects: any[]; w: number; h: number }>({
    rects: [],
    w: 400,
    h: 400,
  });

  // Lưu thông tin ô đang hover và tọa độ chuột hiện tại (tương đối so với container)
  const [activeRect, setActiveRect] = useState<any | null>(null);
  const [mousePos, setMousePos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  useEffect(() => {
    const render = () => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const w = wrap.clientWidth;
      if (w === 0) return;

      // Area follows the square root of the account size: a 10k account still gets a readable cell next to a
      // 200k one, and accounts with a zero/negative balance are no longer dropped (area used to be the balance).
      const areaOf = (m: TreemapSeries) => Math.sqrt(Math.max(m.account_size || 0, m.account_balance || 0, 1000));
      const total = data.reduce((s, m) => s + areaOf(m), 0);
      const smallest = data.length ? Math.min(...data.map(areaOf)) : 1;
      // Grow the box until the smallest cell has roughly 85×65 px.
      const MIN_CELL_AREA = 85 * 65;
      const h = Math.round(Math.min(900, Math.max(400, (MIN_CELL_AREA * total) / (smallest * w))));

      const tmData = data
        .map((m) => {
          const fullName = `${m.first_name} ${m.last_name}`.trim();
          return {
            name: fullName,
            accountNumber: m.account_number,
            value: areaOf(m),
            account_balance: m.account_balance,
            account_size: m.account_size,
            sizeK: Number((m.account_balance / 1000).toFixed(2)),
            pnl: m.pnl_percent,
            dd: m.is_danger ? 7 : 0,
            drawdown: m.drawdown || 0,
            fullName: fullName,
          };
        })
        .sort((a, b) => b.value - a.value);

      // @ts-ignore
      const calculatedRects = squarify(tmData, 0, 0, w, h) || [];
      setLayout({ rects: calculatedRects, w, h });
    };

    render();
    let timer: ReturnType<typeof setTimeout>;
    const onResize = () => {
      clearTimeout(timer);
      timer = setTimeout(render, 150);
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      clearTimeout(timer);
    };
  }, [data]);

  // 🌟 Hàm xử lý khi con trỏ di chuyển bên trong ô thỏi
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>, rect: any) => {
    const wrap = wrapRef.current;
    if (!wrap) return;

    // Lấy vị trí biên của container đồ thị
    const containerRect = wrap.getBoundingClientRect();

    // Tính toán tọa độ chuột tương đối bên trong đồ thị (Chính xác 100%)
    const mouseX = e.clientX - containerRect.left;
    const mouseY = e.clientY - containerRect.top;

    setActiveRect(rect);
    setMousePos({ x: mouseX, y: mouseY });
  };

  // 🌟 Hàm tính toán vị trí Tooltip dựa theo tọa độ chuột hiện tại
  const getTooltipStyle = () => {
    if (!activeRect) return { style: {}, isBottomMode: false, arrowLeft: 0 };

    // Giữ tâm của tooltip trùng hoặc lệch nhẹ so với con trỏ (ví dụ: căn giữa theo trục X của con trỏ)
    let left = mousePos.x - TOOLTIP_WIDTH / 2;

    // Khống chế biên Trái / Phải không cho tràn ra ngoài khung chart
    if (left < 5) left = 5;
    if (left + TOOLTIP_WIDTH > layout.w - 5) left = layout.w - TOOLTIP_WIDTH - 5;

    // Mặc định: Để Tooltip cách con trỏ chuột lên phía trên 15px
    let top = mousePos.y - TOOLTIP_HEIGHT - 25;
    let isBottomMode = false;

    // Nếu con trỏ chuột nằm quá sát mép TRÊN của chart -> Lật Tooltip xuống dưới con trỏ chuột 20px
    if (top < 5) {
      top = mousePos.y + 20;
      isBottomMode = true;
    }

    return {
      style: {
        position: "absolute" as const,
        left: `${left}px`,
        top: `${top}px`,
        width: `${TOOLTIP_WIDTH}px`,
        zIndex: 999,
      },
      isBottomMode,
      // Mũi tên tam giác sẽ chạy theo đúng tọa độ X của chuột
      arrowLeft: mousePos.x - left
    };
  };

  const tooltipConfig = getTooltipStyle();

  return (
    <div
      className="tdv2-treemap"
      ref={wrapRef}
      style={{
        position: "relative",
        width: "100%",
        height: `${layout.h}px`,
        background: "#141b24",
        overflow: "hidden", // 🌟 Ẩn toàn bộ phần lòi ra ngoài của chart thoải mái
      }}
    >
      {/* LAYER BIỂU ĐỒ TRỰC QUAN */}
      {layout.rects.map((r, i) => {
        const bg = pnlToColor(r.pnl);
        const txt = textColorFor(bg);

        const safeWidth = r.rx + r.rw > layout.w ? layout.w - r.rx : r.rw;
        const safeHeight = r.ry + r.rh > layout.h ? layout.h - r.ry : r.rh;
        const small = safeWidth < 90 || safeHeight < 65;
        // Cells this small cannot fit any readable text; the tooltip (or the list view) carries the details.
        const tiny = safeWidth < 46 || safeHeight < 36;

        return (
          <div
            key={i}
            className={`tdv2-tm-cell${small ? " small" : ""}${r.dd >= 7 ? " danger" : ""}`}
            // 🌟 Thay đổi Event ở đây: sử dụng MouseMove và MouseLeave
            onMouseMove={(e) => handleMouseMove(e, r)}
            onMouseLeave={() => setActiveRect(null)}
            onClick={() => onSelect?.(r.accountNumber)}
            style={{
              position: "absolute",
              left: r.rx,
              top: r.ry,
              width: safeWidth,
              height: safeHeight,
              background: bg,
              color: txt,
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              padding: "6px",
              boxSizing: "border-box",
              border: "1px solid rgba(0,0,0,0.15)",
              zIndex: activeRect === r ? 10 : 1,
            }}
          >
            {!tiny && (<>
            <div>
              <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: "12px", fontWeight: "bold" }}>
                {r.name}
              </div>
              {!small && <div style={{ fontSize: "11px", opacity: 0.8, marginTop: "1px" }}>#{r.accountNumber}</div>}
            </div>
            <div>
              <div style={{ fontSize: "14px", fontWeight: "bold" }}>{r.pnl >= 0 ? "+" : ""}{r.pnl}%</div>
              {!small && <div style={{ fontSize: "11px", opacity: 0.8 }}>{r.sizeK}k USD</div>}
            </div>
            </>)}
          </div>
        );
      })}

      {/* LAYER TOOLTIP DI CHUYỂN THEO CON TRỎ CHUỘT */}
      {activeRect && (
        <div
          style={tooltipConfig.style}
          className="pointer-events-none transition-all duration-75 ease-out"
        >
          {/* Mũi tên hướng lên nếu Tooltip nằm dưới chuột */}
          {tooltipConfig.isBottomMode && (
            <svg
              className="block w-3 h-1.5 text-white fill-current drop-shadow-[0_-1px_0_rgba(0,0,0,0.1)] mb-[-1px]"
              style={{ marginLeft: `${tooltipConfig.arrowLeft - 6}px` }}
              viewBox="0 0 12 7"
            >
              <polygon points="6,0 12,7 0,7" />
            </svg>
          )}

          {/* Nội dung Tooltip */}
          <div className="bg-white border border-black/10 rounded-xl p-4 shadow-[0_8px_24px_rgba(0,0,0,0.12)] text-left normal-case font-normal text-slate-800">
            <div className="flex items-center gap-2.5 pb-2.5 border-b border-[#eef0f3] mb-2.5">
              <div className={`w-8 h-8 rounded-full text-xs font-bold flex items-center justify-center shrink-0 ${activeRect.pnl >= 0 ? "bg-green-50 text-green-600" : "bg-red-50 text-red-600"}`}>
                {getInitials(activeRect.name)}
              </div>
              <div className="min-w-0 w-full">
                <div className="text-sm font-semibold text-[#0b1c2e] leading-tight truncate max-w-[140px]">{activeRect.name}</div>
                <div className="text-xs text-[#94a3b8] mt-0.5">#{activeRect.accountNumber}</div>
              </div>
            </div>

            {/* Init Balance */}
            <div className="flex justify-between items-center mb-1.5 text-xs">
              <span className="text-[#64748b]">Số vốn ban đầu</span>
              <span className="text-sm font-semibold text-[#0b1c2e]">{fmtBalance(activeRect.account_size)}</span>
            </div>

            {/* Balance */}
            <div className="flex justify-between items-center mb-1.5 text-xs">
              <span className="text-[#64748b]">Số dư</span>
              <span className="text-sm font-semibold text-[#0b1c2e]">{fmtBalance(activeRect.account_balance ?? activeRect.value)}</span>
            </div>

            <div className="flex justify-between items-center mb-1.5 text-xs">
              <span className="text-[#64748b]">P&amp;L tháng</span>
              <span className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-xs font-semibold ${activeRect.pnl >= 0 ? "bg-green-50 text-green-600" : "bg-red-50 text-red-600"}`}>
                {pnlArrow(activeRect.pnl)}{fmtPnl(activeRect.pnl)}
              </span>
            </div>

            <div className="flex justify-between items-center text-xs">
              <span className="text-[#64748b]">Drawdown</span>
              <span className="text-sm font-semibold" style={{ color: activeRect.drawdown > 3 ? "#a32d2d" : "#334155" }}>
                {(activeRect.drawdown || 0).toFixed(2)}%
              </span>
            </div>
          </div>

          {/* Mũi tên hướng xuống nếu Tooltip nằm trên chuột */}
          {!tooltipConfig.isBottomMode && (
            <svg
              className="block w-3 h-1.5 text-white fill-current drop-shadow-[0_1px_0_rgba(0,0,0,0.1)] mt-[-1px]"
              style={{ marginLeft: `${tooltipConfig.arrowLeft - 6}px` }}
              viewBox="0 0 12 7"
            >
              <polygon points="0,0 12,0 6,7" />
            </svg>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------------------------------------------
// Support queue: who does the leader need to talk to, and how soon.
// Limits follow FTMO: 5% max daily loss and 10% max loss of the initial balance.
// ---------------------------------------------------------------------------------------------------------------
type SupportLevel = "urgent" | "today" | "week" | "positive" | "failed";

const SUPPORT_ORDER: Record<SupportLevel, number> = { urgent: 0, today: 1, week: 2, positive: 3, failed: 4 };
const SUPPORT_LEVELS: SupportLevel[] = ["urgent", "today", "week", "positive", "failed"];
const SUPPORT_LEVEL_KEY: Record<SupportLevel, string> = {
  urgent: "SupportLevel.urgent",
  today: "SupportLevel.today",
  week: "SupportLevel.week",
  positive: "SupportLevel.positive",
  failed: "SupportLevel.failed",
};
const SUPPORT_ICON: Record<SupportLevel, string> = { urgent: "🚨", today: "⚠️", week: "📝", positive: "🎯", failed: "⛔" };

const DAILY_LOSS_LIMIT = 5;
const MAX_LOSS_LIMIT = 10;
const PHASE_TARGET: Record<string, number> = { challenge: 10, verification: 5 };

interface SupportItem {
  id: string;
  level: SupportLevel;
  member: string;
  account: string;
  href?: string;
  reasons: string[];
  dailyUsed: number; // % of the daily loss limit already used
  maxUsed: number; // % of the max loss limit already used
  closeness: number; // sort key inside a level: the closer to a limit, the higher
}

function isFailedAccount(m: TeamDetailTrackingTable): boolean {
  return (m.alerts || []).some((a) => a.toLowerCase().includes("thất bại")) || (m.drawdown || 0) >= MAX_LOSS_LIMIT;
}

function buildSupportItem(m: TeamDetailTrackingTable, t: TFunction): SupportItem | null {
  const dd = m.drawdown || 0;
  const daily = m.daily_drawdown || 0;
  const dailyUsed = (daily / DAILY_LOSS_LIMIT) * 100;
  const maxUsed = (dd / MAX_LOSS_LIMIT) * 100;
  const target = PHASE_TARGET[(m.phase || "").toLowerCase()];
  const health = healthLevel(m.health_status);
  const reasons: string[] = [];
  let level: SupportLevel | null = null;
  const raise = (l: SupportLevel) => {
    if (!level || SUPPORT_ORDER[l] < SUPPORT_ORDER[level]) level = l;
  };

  if (isFailedAccount(m)) {
    level = "failed";
    reasons.push(t("SupportReason.failed", { dd: dd.toFixed(2) }));
  } else {
    if (dailyUsed >= 60) {
      raise("urgent");
      reasons.push(t("SupportReason.dailyLimit", { pct: Math.round(dailyUsed) }));
    } else if (dailyUsed >= 40) {
      raise("today");
      reasons.push(t("SupportReason.dailyLimit", { pct: Math.round(dailyUsed) }));
    }
    if (maxUsed >= 80) {
      raise("urgent");
      reasons.push(t("SupportReason.maxLimit", { pct: Math.round(maxUsed) }));
    } else if (maxUsed >= 60) {
      raise("today");
      reasons.push(t("SupportReason.maxLimit", { pct: Math.round(maxUsed) }));
    }
    if (health === "urgent") {
      raise("today");
      reasons.push(m.health_status);
    }
    if (m.no_sl_count > 0) {
      raise("week");
      reasons.push(t("SupportReason.noSl", { count: m.no_sl_count }));
    }
    if (m.missing_journal >= 2) {
      raise("week");
      reasons.push(t("SupportReason.journal", { count: m.missing_journal }));
    }
    if ((m.roadmap_pnl || 0) < 0) {
      raise("week");
      reasons.push(t("SupportReason.offRoadmap", { pnl: m.roadmap_pnl }));
    }
    // Close to the target with a healthy drawdown: time to protect the result, not to push harder.
    if (!level && target && (m.roadmap_pnl || 0) >= target * 0.8 && dd < 3) {
      level = "positive";
      reasons.push(t("SupportReason.nearTarget", { pnl: m.roadmap_pnl, target }));
    }
  }

  if (!level) return null;
  return {
    id: m.account_number,
    level,
    member: `${m.first_name} ${m.last_name}`.trim(),
    account: m.account_number,
    href: m.platform ? `/${m.platform}/account-live-metrix/${m.account_number}` : undefined,
    reasons,
    dailyUsed,
    maxUsed,
    closeness: Math.max(dailyUsed, maxUsed),
  };
}

const LimitBar: React.FC<{ label: string; used: number }> = ({ label, used }) => {
  const pct = Math.max(0, Math.min(100, used));
  const tone = used >= 80 ? "r" : used >= 50 ? "a" : "g";
  return (
    <div className="tdv2-limit">
      <div className="tdv2-limit-row">
        <span>{label}</span>
        <span className={`v ${tone}`}>{Math.round(used)}%</span>
      </div>
      <div className="tdv2-limit-track">
        <div className={`tdv2-limit-fill ${tone}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const SupportCard: React.FC<{ item: SupportItem }> = ({ item }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? item.reasons : item.reasons.slice(0, 3);
  return (
    <div
      className={`tdv2-support ${item.level}${item.href ? " clickable" : ""}`}
      onClick={() => item.href && navigate(item.href)}
    >
      <div className="tdv2-support-top">
        <span className="ic">{SUPPORT_ICON[item.level]}</span>
        <div className="who">
          <span className="member">{item.member}</span>
          <span className="acc">#{item.account}</span>
        </div>
        <span className={`lvl ${item.level}`}>{t(SUPPORT_LEVEL_KEY[item.level])}</span>
      </div>
      <div className="tdv2-support-reasons">
        {shown.map((r, i) => (
          <span key={i} className="reason">{r}</span>
        ))}
        {item.reasons.length > 3 && (
          <button
            type="button"
            className="reason more"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((v) => !v);
            }}
          >
            {expanded ? "−" : `+${item.reasons.length - 3}`}
          </button>
        )}
      </div>
      {item.level !== "positive" && (
        <div className="tdv2-support-limits">
          <LimitBar label={t("SupportLimit.daily")} used={item.dailyUsed} />
          <LimitBar label={t("SupportLimit.max")} used={item.maxUsed} />
        </div>
      )}
    </div>
  );
};

const TeamDashboardTabV2: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const [filter, setFilter] = useState<TableFilter>("all");
  const [metricFilter, setMetricFilter] = useState<MetricFilter>("all");
  const tableRef = useRef<HTMLDivElement>(null);
  const [period, setPeriod] = useState<"today" | "week" | "month" | "all" | "custom">("custom");
  const [statusOpen, setStatusOpen] = useState(false);
  const [rangeOpen, setRangeOpen] = useState(false);

  const getDefaultDateRange = () => {
    const to = new Date().toISOString().split("T")[0];
    const from = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
    return { from, to };
  };

  const [dateRange, setDateRange] = useState<{ from: string; to: string }>(getDefaultDateRange);
  const [notifOpen, setNotifOpen] = useState(false);
  const [perfView, setPerfView] = useState<PerfView>(readPerfView);
  const narrow = useIsNarrow();
  const navigate = useNavigate();

  const [overview, setOverview] = useState<TeamDetailOverviewApi["data"] | null>(null);
  const [charts, setCharts] = useState<TeamDetailChartDataApi["data"] | null>(null);
  // Member tracker rows for the selected date range.
  const [trackingTable, setTrackingTable] = useState<TeamDetailTrackingTable[]>([]);
  // Rows for the default range, kept separately so the support queue does not change with the table filter.
  const [queueSource, setQueueSource] = useState<TeamDetailTrackingTable[]>([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [queueFilter, setQueueFilter] = useState<SupportLevel | "all">("all");
  const [showFailed, setShowFailed] = useState(false);

  const changePerfView = (view: PerfView) => {
    setPerfView(view);
    try {
      localStorage.setItem(PERF_VIEW_KEY, view);
    } catch {
      // view preference is a convenience only
    }
  };

  // Account number -> platform, needed to open the member's metrix page from the treemap / list.
  const platformByAccount = useMemo(() => {
    const map: Record<string, string> = {};
    [...queueSource, ...trackingTable].forEach((m) => {
      if (m.platform) map[m.account_number] = m.platform;
    });
    return map;
  }, [queueSource, trackingTable]);

  const openAccount = (accountNumber: string) => {
    const platform = platformByAccount[accountNumber];
    if (platform) navigate(`/${platform}/account-live-metrix/${accountNumber}`);
  };

  // Support queue built from the tracking rows (no event API yet), ordered by level, then by how close the
  // account is to an FTMO limit.
  const supportItems = useMemo(
    () =>
      queueSource
        .map((m) => buildSupportItem(m, t))
        .filter((s): s is SupportItem => !!s)
        .sort((a, b) => SUPPORT_ORDER[a.level] - SUPPORT_ORDER[b.level] || b.closeness - a.closeness),
    [queueSource, t]
  );

  const failedAccounts = useMemo(
    () => new Set(queueSource.filter(isFailedAccount).map((m) => m.account_number)),
    [queueSource]
  );

  const supportCounts = useMemo(() => {
    const c: Record<SupportLevel, number> = { urgent: 0, today: 0, week: 0, positive: 0, failed: 0 };
    supportItems.forEach((s) => (c[s.level] += 1));
    return c;
  }, [supportItems]);

  // "All" hides failed accounts: they need a different conversation (review / restart), not risk triage.
  const visibleSupport = supportItems.filter((s) =>
    queueFilter === "all" ? s.level !== "failed" : s.level === queueFilter
  );

  const [dataStartDate, setDataStartDate] = useState<string>("");
  const [dataEndDate, setDataEndDate] = useState<string>("");

  const [tableStartDate, setTableStartDate] = useState<string>(() => {
    return new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
  });
  const [tableEndDate, setTableEndDate] = useState<string>(() => {
    return new Date().toISOString().split("T")[0];
  });

  const [loading, setLoading] = useState(true);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClose = () => {
      setStatusOpen(false);
      setRangeOpen(false);
    };
    window.addEventListener("click", handleClose);
    return () => window.removeEventListener("click", handleClose);
  }, []);

  // Overview, charts and the support queue load once per team. Only the member tracker follows the date range,
  // so changing the range no longer refetches everything nor blanks the whole tab behind a spinner.
  // (The unused /dashboard/metrics call was dropped.)
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    Promise.allSettled([
      getTeamDetailOverview(Number(id)),
      getTeamDetailCharts(Number(id)),
      getTeamDetailTrackingTable(Number(id), tableStartDate, tableEndDate),
    ]).then(([ov, ch, tr]) => {
      if (cancelled) return;
      if (ov.status === "fulfilled" && ov.value.success) setOverview(ov.value.data || null);
      if (ch.status === "fulfilled" && ch.value.success) setCharts(ch.value.data);
      if (tr.status === "fulfilled" && tr.value.success) {
        setQueueSource(tr.value.data || []);
        setTrackingTable(tr.value.data || []);
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const firstTableLoad = useRef(true);
  useEffect(() => {
    if (!id) return;
    if (firstTableLoad.current) {
      firstTableLoad.current = false; // already loaded together with the overview
      return;
    }
    let cancelled = false;
    setTableLoading(true);
    getTeamDetailTrackingTable(Number(id), tableStartDate, tableEndDate)
      .then((tr) => {
        if (!cancelled && tr.success) setTrackingTable(tr.data || []);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setTableLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, tableStartDate, tableEndDate]);

  const dashboardSummary = useMemo<DashboardSummary | null>(() => {
    // Never fall back to mock numbers: a failed request must not look like real team data.
    if (!overview) return null;
    return {
      totalMembers: overview.total_members,
      growing: overview.growing_accounts,
      danger: overview.at_risk_accounts,
      funded: overview.funded_accounts,
      passable: overview.passing_potential,
      monthlyLosses: overview.losing_trades,
      slViolations: overview.sl_violations,
      missingJournal: overview.missing_journal,
    };
  }, [overview]);

  const dashboardMembers = useMemo<DashboardMember[]>(() => {
    if (!trackingTable.length) return [];
    return trackingTable.map((m) => {
      const stageMap: Record<string, MemberStage> = {
        funded: "funded",
        challenge: "challenge",
        verification: "verification",
        new: "new",
        newbie: "new",
      };
      return {
        id: m.account_number.slice(-3).toUpperCase(),
        name: `${m.first_name} ${m.last_name}`,
        avatar_url: m.avatar_url,
        shortName: m.last_name,
        size: m.account_size.toFixed(0),
        sizeK: m.account_size / 1000,
        stage: stageMap[m.phase?.toLowerCase()] || "new",
        account_number: m.account_number,
        pnl: m.pnl_week,
        dd: m.drawdown,
        road: m.roadmap_pnl || 0,
        roadTarget: 10,
        sl: m.no_sl_count,
        journal: m.missing_journal,
        avColor: "#1A5FA8",
        avBg: "#E8F1FC",
        passScore: m.roadmap_pnl || 0,
        losses: 0,
        totalTrades: 0,
        healthStatus: m.health_status,
        alerts: m.alerts,
        platform: m.platform || '',
      };
    });
  }, [trackingTable]);

  const stageDistribution = useMemo(() => {
    if (!charts?.distribution?.phases) return [];
    const p = charts.distribution.phases;
    return [
      { label: "Funded", value: p.funded, color: "#2D7A3A" },
      { label: "Challenge", value: p.challenge, color: "#1A5FA8" },
      { label: "Verification", value: p.verification, color: "#5B3FA8" },
      { label: "Mới", value: p.newbie, color: "#9B9589" },
    ];
  }, [charts]);


  // Per-account numbers from the charts API, used by the overview-card filters.
  const chartCounts = useMemo(() => {
    const losses: Record<string, number> = {};
    const pass: Record<string, number> = {};
    const lt = charts?.member_charts?.losing_trades;
    lt?.account_numbers?.forEach((acc, i) => (losses[acc] = Number(lt.data?.[i]) || 0));
    const pp = charts?.member_charts?.pass_probability;
    pp?.account_numbers?.forEach((acc, i) => (pass[acc] = Number(pp.data?.[i]) || 0));
    return { losses, pass };
  }, [charts]);

  // Matches the "Growing" card: health says so, or the account is above its starting point on the roadmap.
  const isGrowing = (m: DashboardMember) => {
    if (failedAccounts.has(m.account_number)) return false;
    const h = (m.healthStatus || "").toLowerCase();
    return h.includes("tăng trưởng") || h.includes("phát triển") || h.includes("growing") || m.road > 0;
  };

  const filteredMembers = useMemo(() => {
    const rows = dashboardMembers.filter((m) => {
      // 1. Filter by status
      if (filter !== "all") {
        if (filter === "funded" && m.stage !== "funded") return false;
        if (filter === "challenge" && m.stage !== "challenge") return false;
        if (filter === "verification" && m.stage !== "verification") return false;
        if (filter === "danger" && !isDangerMember(m)) return false;
      }

      // 2. Filter from the overview card the user clicked
      switch (metricFilter) {
        case "growing": return isGrowing(m);
        case "danger": return isDangerMember(m) || failedAccounts.has(m.account_number);
        case "funded": return m.stage === "funded";
        case "pass": return (chartCounts.pass[m.account_number] || 0) > 0;
        case "losses": return (chartCounts.losses[m.account_number] || 0) > 0;
        case "noSl": return m.sl > 0;
        case "noJournal": return m.journal > 0;
        default: return true;
      }
    });

    // Count-based cards sort by that count (worst first); everything else keeps failed accounts last.
    const sortKey: ((m: DashboardMember) => number) | null =
      metricFilter === "losses" ? (m) => chartCounts.losses[m.account_number] || 0
        : metricFilter === "noSl" ? (m) => m.sl
          : metricFilter === "noJournal" ? (m) => m.journal
            : metricFilter === "pass" ? (m) => chartCounts.pass[m.account_number] || 0
              : null;
    return sortKey
      ? rows.sort((a, b) => sortKey(b) - sortKey(a))
      : rows.sort((a, b) => Number(failedAccounts.has(a.account_number)) - Number(failedAccounts.has(b.account_number)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, metricFilter, dashboardMembers, failedAccounts, chartCounts]);

  // Member tracker pagination (client side; the API returns the whole team at once).
  const [tablePage, setTablePage] = useState(1);
  const [tablePageSize, setTablePageSize] = useState(10);
  useEffect(() => {
    setTablePage(1);
  }, [filter, metricFilter, tableStartDate, tableEndDate]);
  const pagedMembers = filteredMembers.slice((tablePage - 1) * tablePageSize, tablePage * tablePageSize);

  const applyMetricFilter = (key: MetricFilter) => {
    setMetricFilter((prev) => (prev === key || key === "all" ? "all" : key));
    setFilter("all");
    // Bring the filtered table into view.
    setTimeout(() => tableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  const exportMembersCsv = () => {
    const header = [t("Member"), "Account", t("Stage"), "Drawdown %", "Roadmap %", t("NoStopLoss"), t("MissingJournal"), t("AccountHealth"), t("Warnings")];
    const rows = filteredMembers.map((m) => [
      m.name, m.account_number, m.stage, m.dd, m.road, m.sl, m.journal,
      failedAccounts.has(m.account_number) ? t("SupportLevel.failed") : m.healthStatus || "",
      (m.alerts || []).join(" | "),
    ]);
    const esc = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [header, ...rows].map((r) => r.map(esc).join(",")).join("\r\n");
    // BOM so Excel opens Vietnamese text correctly.
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `team-${id}-members-${tableStartDate || "all"}_${tableEndDate || ""}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Chart data
  const stageChartData = useMemo(() => ({
    labels: stageDistribution.map((s) => s.label),
    datasets: [{ data: stageDistribution.map((s) => s.value), backgroundColor: stageDistribution.map((s) => s.color), borderWidth: 0, hoverOffset: 4 }],
  }), [stageDistribution]);

  const pnlGroups = useMemo(() => {
    if (charts?.distribution?.trades) {
      return {
        Lãi: charts.distribution.trades.win,
        Lỗ: charts.distribution.trades.loss,
        Hòa: charts.distribution.trades.breakeven,
      };
    }
    return { Lãi: 0, Lỗ: 0, Hòa: 0 };
  }, [charts]);

  const passChanceDataFromChart = useMemo(() => {
    if (!charts?.member_charts?.pass_probability) return [];
    const { account_numbers, first_name, last_name, data } = charts.member_charts.pass_probability;
    if (!account_numbers) return [];
    // The API returns a 0–1 probability (e.g. 0.35) while the axis is 0–100%, which made every bar invisible.
    const raw = account_numbers.map((_, i) => Number(data?.[i]) || 0);
    const scale = Math.max(0, ...raw) <= 1 ? 100 : 1;
    return account_numbers.map((account, i) => ({
      shortName: account,
      passScore: Math.round(raw[i] * scale * 10) / 10,
      label: `${first_name?.[i] || ""} ${last_name?.[i] || ""}`
    }));
  }, [charts]);

  const passChanceSorted = useMemo(
    () => [...passChanceDataFromChart].sort((a, b) => b.passScore - a.passScore),
    [passChanceDataFromChart]
  );
  const passScores = passChanceSorted.map((p) => p.passScore);
  const passNames = passChanceSorted.map((p) => p.shortName);
  const hasPassData = passScores.some((v) => v > 0);

  // Failed accounts (max loss hit) distort the map and the colour scale, so they are hidden unless asked for.
  const allPerfSeries = useMemo(
    () => charts?.treemap?.series.filter((s) => s.account_size !== 0) || [],
    [charts]
  );
  const hiddenFailedCount = allPerfSeries.filter((s) => failedAccounts.has(s.account_number)).length;
  const perfSeries = useMemo(
    () => (showFailed ? allPerfSeries : allPerfSeries.filter((s) => !failedAccounts.has(s.account_number))),
    [allPerfSeries, failedAccounts, showFailed]
  );

  // Inline rail (tablet / phone): fewer cards and no inner scroll on phones, "View all" opens the full list.
  const inlineLimit = narrow ? 3 : TOP_NOTIF_LIMIT;

  const renderQueueFilters = () => (
    <div className="tdv2-queue-filters">
      {(["all", ...SUPPORT_LEVELS] as (SupportLevel | "all")[]).map((f) => {
        const count = f === "all" ? supportItems.length - supportCounts.failed : supportCounts[f];
        if (f !== "all" && count === 0) return null;
        return (
          <button
            key={f}
            type="button"
            className={`${queueFilter === f ? "active" : ""} ${f}`}
            onClick={() => setQueueFilter(f)}
          >
            {f === "all" ? t("Teams_dashboard.filter.All") : t(SUPPORT_LEVEL_KEY[f])} ({count})
          </button>
        );
      })}
    </div>
  );

  const renderSupportList = (compact: boolean, limit?: number) =>
    visibleSupport.length === 0 ? (
      <div className={`tdv2-notif-empty${compact ? " compact" : ""}`}>
        <div className="ic-wrap">
          <CheckSquareOffset size={compact ? 22 : 32} className="text-green-500" />
        </div>
        <div>
          <h4>{t("Teams_dashboard.team_topNotifications_noData_title")}</h4>
          <p>{t("Teams_dashboard.team_topNotifications_noData_desc")}</p>
        </div>
      </div>
    ) : (
      (limit ? visibleSupport.slice(0, limit) : visibleSupport).map((s) => <SupportCard key={s.id} item={s} />)
    );

  // Team-wide win / loss totals (always available) for the win-rate summary above the chart.
  const teamTrades = useMemo(() => {
    const tr = charts?.distribution?.trades;
    const win = Number(tr?.win) || 0;
    const loss = Number(tr?.loss) || 0;
    const be = Number(tr?.breakeven) || 0;
    const total = win + loss + be;
    return { win, loss, be, total, winRate: total ? (win / total) * 100 : 0 };
  }, [charts]);

  // Wins vs losses per member. The API only sends losing trades per member today; when `winning_trades`
  // arrives the chart turns into stacked win/loss bars with a win rate per member.
  const tradesByMember = useMemo(() => {
    const lt = charts?.member_charts?.losing_trades;
    if (!lt?.account_numbers || !lt.data) return { hasWins: false, data: { labels: [], datasets: [] } };
    const wt = charts?.member_charts?.winning_trades;
    const winsByAcc: Record<string, number> = {};
    wt?.account_numbers?.forEach((acc, i) => (winsByAcc[acc] = Number(wt.data?.[i]) || 0));
    const hasWins = !!wt?.account_numbers?.length;

    const rows = lt.account_numbers
      .map((acc, i) => ({
        acc,
        name: `${lt.first_name?.[i] || ""} ${lt.last_name?.[i] || ""}`.trim(),
        loss: Number(lt.data?.[i]) || 0,
        win: winsByAcc[acc] || 0,
      }))
      .filter((r) => r.loss + r.win > 0)
      .sort((a, b) => b.loss + b.win - (a.loss + a.win));

    const names = rows.map((r) => r.name);
    const datasets: any[] = hasWins
      ? [
          { label: t("WinningTrades"), data: rows.map((r) => r.win), names, backgroundColor: "#2D9B45", borderRadius: 4, borderSkipped: false, stack: "t" },
          { label: t("LosingTrades"), data: rows.map((r) => r.loss), names, backgroundColor: "#E24B4A", borderRadius: 4, borderSkipped: false, stack: "t" },
        ]
      : [
          {
            label: t("LosingTrades"),
            data: rows.map((r) => r.loss),
            names,
            backgroundColor: rows.map((r) => (r.loss >= 9 ? "#E24B4A" : r.loss >= 6 ? "#EF9F27" : "#B4B2A9")),
            borderRadius: 4,
            borderSkipped: false,
          },
        ];
    return { hasWins, rows, data: { labels: rows.map((r) => r.acc), datasets } };
  }, [charts, t]);

  const violationsData = useMemo(() => {
    if (!charts?.member_charts?.violations) return { labels: [], datasets: [] };
    const { account_numbers, no_sl, missing_journal } = charts.member_charts.violations;
    if (!account_numbers) return { labels: [], datasets: [] };

    const safeNoSl = no_sl || [];
    const safeMissingJournal = missing_journal || [];

    // 1. Gộp dữ liệu của từng tài khoản lại và lọc các tài khoản có vi phạm (> 0)
    const combinedData = account_numbers
      .map((acc, index) => {
        const journalVal = safeMissingJournal[index] || 0;
        const noSlVal = safeNoSl[index] || 0;

        // Tìm tên thành viên tương ứng
        const m = dashboardMembers.find((member) => member.account_number === acc || member.id === acc);
        const name = m ? m.name : acc;

        return {
          label: acc,
          noSl: noSlVal,
          missingJournal: journalVal,
          name: name,
        };
      })
      // Tài khoản được giữ lại nếu ít nhất có 1 loại vi phạm > 0
      .filter((item) => item.noSl > 0 || item.missingJournal > 0);

    // 2. Sắp xếp: Ưu tiên missingJournal giảm dần, nếu bằng nhau thì xét noSl giảm dần
    combinedData.sort((a, b) => {
      if (b.missingJournal !== a.missingJournal) {
        return b.missingJournal - a.missingJournal; // Thằng nào thiếu journal nhiều hơn lên trước
      }
      return b.noSl - a.noSl; // Nếu thiếu journal bằng nhau, thằng nào no_sl nhiều hơn lên trước
    });

    // 3. Tách ngược lại thành các mảng đơn để đưa vào Chart
    const sortedLabels = combinedData.map((item) => item.label);
    const sortedNoSl = combinedData.map((item) => item.noSl);
    const sortedMissingJournal = combinedData.map((item) => item.missingJournal);
    const sortedNames = combinedData.map((item) => item.name);

    return {
      labels: sortedLabels,
      datasets: [
        {
          label: t("NoStopLoss"),
          data: sortedNoSl,
          names: sortedNames,
          backgroundColor: "#E24B4A",
          borderRadius: 4,
          borderSkipped: false,
        } as any,
        {
          label: t("MissingJournal"),
          data: sortedMissingJournal,
          names: sortedNames,
          backgroundColor: "#EF9F27",
          borderRadius: 4,
          borderSkipped: false,
        } as any,
      ],
    };
  }, [charts, t, dashboardMembers]);

  const baseBarOpts = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (context: any) => {
            const ds = context[0].dataset;
            return ds.names ? ds.names[context[0].dataIndex] : context[0].label;
          },
        },
      },
    },
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="team-dash-v2">

      <div className="tdv2-main-grid">

        {/* LEFT COLUMN */}
        <div className="tdv2-main-left">
          <div className="tdv2-section-hd">{t("TeamOverview")}</div>
          <div className="tdv2-metrics">
            {([
              { key: "all", tip: "Teams_dashboard.Tooltip.totalMembers", lbl: t("TotalMembers"), val: dashboardSummary?.totalMembers, tone: "gr", sub: t("Active") },
              { key: "growing", tip: "Teams_dashboard.Tooltip.growing", lbl: t("Growing"), val: dashboardSummary?.growing, tone: "g", sub: t("ProfitableAccounts") },
              { key: "danger", tip: "Teams_dashboard.Tooltip.danger", lbl: t("Danger"), val: dashboardSummary?.danger, tone: "r", sub: t("NeedIntervention") },
              { key: "funded", tip: "Teams_dashboard.Tooltip.funded", lbl: "Funded", val: dashboardSummary?.funded, tone: "b", sub: t("ReceivingPayout") },
              { key: "pass", tip: "Teams_dashboard.Tooltip.passable", lbl: t("PassChance"), val: dashboardSummary?.passable, tone: "g", sub: t("GoodRoadmap") },
              { key: "losses", tip: "Teams_dashboard.Tooltip.monthlyLosses", lbl: t("LossesPerMonth"), val: dashboardSummary?.monthlyLosses, tone: "a", sub: t("TeamTotal") },
              { key: "noSl", tip: "Teams_dashboard.Tooltip.slViolations", lbl: t("SLViolations"), val: dashboardSummary?.slViolations, tone: "r", sub: t("NoStopLoss") },
              { key: "noJournal", tip: "Teams_dashboard.Tooltip.missingJournal", lbl: t("NoJournal"), val: dashboardSummary?.missingJournal, tone: "a", sub: t("MissingJournal") },
            ] as { key: MetricFilter; tip: string; lbl: string; val?: number; tone: string; sub: string }[]).map((c) => (
              <button
                type="button"
                key={c.key}
                className={`tdv2-metric${metricFilter === c.key && c.key !== "all" ? " active" : ""}`}
                data-tooltip={`${t(c.tip)} · ${t("ClickToFilter")}`}
                onClick={() => applyMetricFilter(c.key)}
              >
                <div className="lbl">{c.lbl}</div>
                <div className={`val ${c.tone}`}>{c.val ?? "—"}</div>
                <div className="sub-val">{c.sub}</div>
              </button>
            ))}
          </div>

          <div className="tdv2-notif-rail-inline">
            <div className="tdv2-section-hd">
              <span>{t("ActionNeededNow")}</span>
              {visibleSupport.length > inlineLimit && (
                <button className="tdv2-view-all" onClick={() => setNotifOpen(true)}>
                  {t("ViewAll")} ({visibleSupport.length})
                </button>
              )}
            </div>
            {renderQueueFilters()}
            <div className="tdv2-notif-list">{renderSupportList(true, inlineLimit)}</div>
          </div>

          <div className="tdv2-section-hd">{t("MemberPerformanceStage")}</div>
          <div className="tdv2-card">
            <div className="tdv2-perf-head">
              <div className="min-w-0">
                <div className="tdv2-card-title">
                  {perfView === "map" ? t("PerformanceTreemap") : t("PerformanceTreemap").replace(/\s*\(Treemap\)\s*$/i, "")}
                </div>
                <div className="tdv2-card-sub">{perfView === "map" ? t("TreemapHint") : t("PerformanceListHint")}</div>
              </div>
              <div className="tdv2-view-toggle" role="tablist">
                <button type="button" role="tab" aria-selected={perfView === "map"} className={perfView === "map" ? "active" : ""} onClick={() => changePerfView("map")}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="10" height="18" rx="1" /><rect x="15" y="3" width="6" height="8" rx="1" /><rect x="15" y="13" width="6" height="8" rx="1" /></svg>
                  {t("TreemapViewMap")}
                </button>
                <button type="button" role="tab" aria-selected={perfView === "list"} className={perfView === "list" ? "active" : ""} onClick={() => changePerfView("list")}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></svg>
                  {t("TreemapViewList")}
                </button>
              </div>
            </div>
            {perfView === "map" ? (
              <>
                <Treemap data={perfSeries} onSelect={openAccount} />
                <div className="tdv2-tm-scale">
                  <span>{t("BigLoss")}</span>
                  <div className="tdv2-tm-scale-bar" />
                  <span>{t("BigProfit")}</span>
                </div>
              </>
            ) : (
              <PerformanceList data={perfSeries} onSelect={openAccount} />
            )}
            {(hiddenFailedCount > 0 || showFailed) && (
              <button type="button" className="tdv2-failed-toggle" onClick={() => setShowFailed((v) => !v)}>
                {showFailed ? t("HideFailedAccounts") : t("FailedAccountsHidden", { count: hiddenFailedCount })}
              </button>
            )}
          </div>
          <div className="tdv2-charts-full">
            <div className="tdv2-card">
              <div className="tdv2-stage-trades">
                <div className="tdv2-stage-col">
                  <div className="tdv2-card-title">{t("StageDistribution")}</div>
                  <div className="tdv2-card-sub">{t("AccountStatus")}</div>
                  <div className="tdv2-donut-wrap" style={{ height: 140, marginBottom: 10 }}>
                    <Doughnut data={stageChartData} options={{ ...baseBarOpts, cutout: "68%" } as any} />
                    <div className="tdv2-donut-center">
                      <div className="big">{charts?.distribution.phases?.total || 0}</div>
                      <div className="sm">TK</div>
                    </div>
                  </div>
                  <div className="tdv2-stage-legend">
                    {stageDistribution.map((s) => (
                      <div className="chip" key={s.label} style={{ background: `${s.color}1a` }}>
                        <span className="dot" style={{ background: s.color }} />
                        <span className="n" style={{ color: s.color }}>{s.value || 0}</span>
                        <span>{s.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="tdv2-trades-col">
                  <div className="tdv2-card-title">{t("TradesByGroup")}</div>
                  <div className="tdv2-card-sub">{t("AccountStatus")}</div>
                  <div style={{ height: 200 }}>
                    <Bar
                      data={{
                        labels: ["Lãi", "Lỗ", "Hòa"],
                        datasets: [{ data: [pnlGroups["Lãi"], pnlGroups["Lỗ"], pnlGroups["Hòa"]], backgroundColor: ["#2D9B45", "#E24B4A", "#9B9589"], borderRadius: 4, borderSkipped: false }],
                      }}
                      options={{ ...baseBarOpts, scales: { y: { display: false }, x: { ticks: { font: { size: 13, weight: 600 } }, grid: { display: false } } } } as any}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="tdv2-card">
              <div className="tdv2-card-title">{t("PassChanceFTMO")}</div>
              <div className="tdv2-card-sub">{t("PassChanceHint")}</div>
              {/* No data: show the message only, not an empty 0–100% grid. */}
              {!hasPassData ? (
                <div className="tdv2-chart-empty">{t("NoPassChanceData")}</div>
              ) : narrow ? (
                <div style={{ height: hBarHeight(passNames.length) }}>
                  <Bar
                    data={{ labels: passChanceSorted.map((p) => shortName(p.label.trim() || p.shortName)), datasets: [{ data: passScores, names: passChanceSorted.map((p) => `${p.label} (#${p.shortName})`), backgroundColor: passScores.map((v) => (v >= 70 ? "#1B7A37" : v >= 40 ? "#2D9B45" : "#8FD19E")), borderRadius: 4, borderSkipped: false } as any] }}
                    options={{ ...baseBarOpts, indexAxis: "y", scales: { x: { min: 0, max: 100, ticks: { callback: (v: any) => v + "%", font: { size: 10 } }, grid: { color: "rgba(128,128,128,0.12)" } }, y: { ticks: { font: { size: 11 }, autoSkip: false }, grid: { display: false } } } } as any}
                  />
                </div>
              ) : (
                // Scroll horizontally when there are many traders so each bar stays readable.
                <div className="tdv2-bar-scroll">
                  <div style={{ height: 240, minWidth: Math.max(passNames.length * 44, 320) }}>
                    <Bar
                      data={{ labels: passNames, datasets: [{ data: passScores, names: passChanceSorted.map(p => p.label), backgroundColor: passScores.map((v) => (v >= 70 ? "#1B7A37" : v >= 40 ? "#2D9B45" : "#8FD19E")), borderRadius: 4, borderSkipped: false } as any] }}
                      options={{ ...baseBarOpts, scales: { y: { min: 0, max: 100, ticks: { callback: (v: any) => v + "%", font: { size: 10 } }, grid: { color: "rgba(128,128,128,0.12)" } }, x: { ticks: { font: { size: 10 }, maxRotation: 60, minRotation: 45, autoSkip: false }, grid: { display: false } } } } as any}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="tdv2-charts-group">
            <div className="tdv2-card">
              <div className="tdv2-card-title">{t("WinLossTrades")}</div>
              <div className="tdv2-card-sub">{t("WinLossHint")}</div>
              {teamTrades.total === 0 ? (
                <div className="tdv2-chart-empty">{t("NoData")}</div>
              ) : (
                <div className="tdv2-wl">
                  {/* Team summary: donut with the win rate in the middle */}
                  <div className="tdv2-wl-summary">
                    <div className="tdv2-wl-donut">
                      <Doughnut
                        data={{
                          labels: [t("WinShort"), t("LossShort"), t("BreakevenShort")],
                          datasets: [{
                            data: [teamTrades.win, teamTrades.loss, teamTrades.be],
                            backgroundColor: ["#2D9B45", "#E24B4A", "#B4B2A9"],
                            borderWidth: 0,
                            hoverOffset: 3,
                          }],
                        }}
                        options={{ responsive: true, maintainAspectRatio: false, cutout: "74%", plugins: { legend: { display: false } } } as any}
                      />
                      <div className="center">
                        <b>{teamTrades.winRate.toFixed(1)}%</b>
                        <span>{t("WinRate")}</span>
                      </div>
                    </div>
                    <div className="tdv2-wl-stats">
                      <div className="s w">
                        <span className="dot" />
                        <span className="k">{t("WinShort")}</span>
                        <b>{teamTrades.win}</b>
                        <small>{((teamTrades.win / teamTrades.total) * 100).toFixed(1)}%</small>
                      </div>
                      <div className="s l">
                        <span className="dot" />
                        <span className="k">{t("LossShort")}</span>
                        <b>{teamTrades.loss}</b>
                        <small>{((teamTrades.loss / teamTrades.total) * 100).toFixed(1)}%</small>
                      </div>
                      {teamTrades.be > 0 && (
                        <div className="s b">
                          <span className="dot" />
                          <span className="k">{t("BreakevenShort")}</span>
                          <b>{teamTrades.be}</b>
                          <small>{((teamTrades.be / teamTrades.total) * 100).toFixed(1)}%</small>
                        </div>
                      )}
                      <div className="total">{t("TotalTradesCount", { count: teamTrades.total })}</div>
                    </div>
                  </div>

                  {/* Per member ranking: stacked win/loss bars when the API sends wins, otherwise losses only */}
                  {(tradesByMember.rows || []).length > 0 && (
                    <>
                      <div className="tdv2-wl-list-hd">
                        <span>{tradesByMember.hasWins ? t("WinLossByMember") : t("LossesByMember")}</span>
                        <span className="n">{(tradesByMember.rows || []).length}</span>
                      </div>
                      <div className="tdv2-wl-list">
                        {(() => {
                          const rows = tradesByMember.rows || [];
                          const max = Math.max(1, ...rows.map((r) => r.win + r.loss));
                          return rows.map((r, i) => {
                            const total = r.win + r.loss;
                            return (
                              <div
                                key={r.acc}
                                className={`tdv2-wl-row${platformByAccount[r.acc] ? " clickable" : ""}`}
                                onClick={() => openAccount(r.acc)}
                              >
                                <span className="rank">{i + 1}</span>
                                <div className="who">
                                  <div className="nm">{r.name || `#${r.acc}`}</div>
                                  <div className="acc">#{r.acc}</div>
                                </div>
                                <div className="bar" style={{ width: `${(total / max) * 100}%` }}>
                                  {r.win > 0 && <div className="w" style={{ flex: r.win }} />}
                                  {r.loss > 0 && <div className="l" style={{ flex: r.loss }} />}
                                </div>
                                <div className="val">
                                  {tradesByMember.hasWins ? (
                                    <>
                                      <span className="w">{r.win}</span>/<span className="l">{r.loss}</span>
                                      <small>{total ? ((r.win / total) * 100).toFixed(0) : 0}%</small>
                                    </>
                                  ) : (
                                    <span className="l">{r.loss}</span>
                                  )}
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="tdv2-card">
              <div className="tdv2-card-title">{t("DisciplineViolations")}</div>
              <div className="tdv2-card-sub">{t("DisciplineHint")}</div>
              {/* Totals row, mirrors the win/loss summary in the card next to it */}
              {(() => {
                const sum = (i: number) =>
                  ((violationsData.datasets[i] as any)?.data || []).reduce((s: number, v: any) => s + (Number(v) || 0), 0);
                const noSl = sum(0);
                const journal = sum(1);
                if (!noSl && !journal) return null;
                return (
                  <div className="tdv2-viol-totals">
                    <div className="t r">
                      <span className="dot" />
                      <span className="k" title={t("NoStopLoss")}>{t("ViolNoSlShort")}</span>
                      <b>{noSl}</b>
                    </div>
                    <div className="t a">
                      <span className="dot" />
                      <span className="k">{t("MissingJournal")}</span>
                      <b>{journal}</b>
                    </div>
                    <div className="t n">
                      <span className="k" title={t("MembersWithViolations")}>{t("ViolMembersShort")}</span>
                      <b>{violationsData.labels.length}</b>
                    </div>
                  </div>
                );
              })()}
              {narrow ? (
                <div style={{ height: hBarHeight(violationsData.labels.length) + 24 }}>
                  <Bar
                    data={{
                      ...violationsData,
                      labels: ((violationsData.datasets[0] as any)?.names || violationsData.labels).map((n: string) => shortName(String(n))),
                    }}
                    options={{ ...baseBarOpts, indexAxis: "y", plugins: { ...baseBarOpts.plugins, legend: { display: false } }, scales: { y: { ticks: { font: { size: 11 }, autoSkip: false }, grid: { display: false } }, x: { ticks: { font: { size: 10 } }, grid: { color: "rgba(128,128,128,0.12)" } } } } as any}
                  />
                </div>
              ) : (
                // Fills the card so it matches the height of the win/loss card beside it.
                <div className="tdv2-fill-chart">
                  <Bar
                    data={violationsData}
                    options={{ ...baseBarOpts, plugins: { ...baseBarOpts.plugins, legend: { display: false } }, scales: { x: { ticks: { font: { size: 11 } }, grid: { display: false } }, y: { ticks: { font: { size: 10 } }, grid: { color: "rgba(128,128,128,0.12)" } } } } as any}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div className="tdv2-main-right">
          <div className="tdv2-section-hd">
            <span>{t("ActionNeededNow")}</span>
            {visibleSupport.length > TOP_NOTIF_LIMIT && (
              <button className="tdv2-view-all" onClick={() => setNotifOpen(true)}>
                {t("ViewAll")} ({visibleSupport.length})
              </button>
            )}
          </div>
          {renderQueueFilters()}
          <div className="tdv2-notif-list">{renderSupportList(false, TOP_NOTIF_LIMIT)}</div>
        </div>

      </div>{/* end .tdv2-main-grid */}

      {/* MEMBER TABLE: full width */}
      <div className="tdv2-section-hd" ref={tableRef}>{t("MemberDetails")}</div>
      <div className="tdv2-table-card">
        <div className="tdv2-table-head">
          <div>
            <div className="t">{t("MemberTracker")}</div>
            <div className="s">{t("UpdatedLastSession")}</div>
          </div>

          {metricFilter !== "all" && (
            <button type="button" className="tdv2-active-filter" onClick={() => setMetricFilter("all")} title={t("ClearFilter")}>
              {t("FilteringBy")}: <b>{{
                growing: t("Growing"),
                danger: t("Danger"),
                funded: "Funded",
                pass: t("PassChance"),
                losses: t("LossesPerMonth"),
                noSl: t("SLViolations"),
                noJournal: t("NoJournal"),
              }[metricFilter]}</b> ({filteredMembers.length}) <span aria-hidden>✕</span>
            </button>
          )}

          <div className="filterbar">
            <div className="filter-left">
              <div className={`status-wrap${statusOpen ? " open" : ""}`} onClick={(e) => e.stopPropagation()}>
                <button className="status-btn" onClick={() => { setStatusOpen(!statusOpen); setRangeOpen(false); }}>
                  <span className="dot-status" />
                  <span>
                    {filter === "all" ? t("Teams_dashboard.filter.AllStatuses") :
                      filter === "funded" ? t("Teams_dashboard.filter.Funded") :
                        filter === "challenge" ? t("Teams_dashboard.filter.Challenge") :
                          filter === "verification" ? t("Teams_dashboard.filter.Verification") :
                            filter === "danger" ? t("Danger") : t("Teams_dashboard.filter.All")}
                  </span>
                  <svg className="chev" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
                </button>
                <div className="status-menu">
                  {(["all", "funded", "challenge", "verification", "danger"] as TableFilter[]).map((f) => (
                    <button
                      key={f}
                      className={filter === f ? "sel" : f === "danger" ? "danger-opt" : ""}
                      onClick={() => { setFilter(f); setStatusOpen(false); }}
                    >
                      <span className="dot-status" />
                      {f === "all" ? t("Teams_dashboard.filter.All") : f === "danger" ? `⚠ ${t("Danger")}` : f === "funded" ? t("Teams_dashboard.filter.Funded") : f === "challenge" ? t("Teams_dashboard.filter.Challenge") : t("Teams_dashboard.filter.Verification")}
                    </button>
                  ))}
                </div>
              </div>

              <div className="divider" />

              {/* <div className="segmented">
                {(["all", "today", "week", "month"] as const).map((p) => (
                  <button
                    key={p}
                    className={period === p ? "active" : ""}
                    onClick={() => {
                      setPeriod(p);
                      setDateRange({ from: "", to: "" });
                      if (p === "all") {
                        setTableStartDate("all");
                        setTableEndDate("");
                      } else {
                        const now = new Date();
                        const to = now.toISOString().split('T')[0];
                        let from = to;
                        if (p === 'week') {
                          // Get Sunday of the current week
                          const day = now.getDay(); // 0 is Sunday, 1 is Monday...
                          const diff = now.getDate() - day;
                          const sun = new Date(now.setDate(diff));
                          from = sun.toISOString().split('T')[0];
                        } else if (p === 'month') {
                          from = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
                        }
                        setTableStartDate(from);
                        setTableEndDate(to);
                      }
                    }}
                  >
                    {p === "today" ? t("Teams_dashboard.filter.Today") :
                      p === "week" ? t("Teams_dashboard.filter.ThisWeek") :
                        p === "month" ? t("Teams_dashboard.filter.ThisMonth") : t("Teams_dashboard.filter.All")}
                  </button>
                ))}
              </div> */}

              <div className={`range-wrap${rangeOpen ? " open" : ""}`} onClick={(e) => e.stopPropagation()}>
                <button className="range-btn" title={t("Teams_dashboard.filter.CustomDateRange")} onClick={() => { setRangeOpen(!rangeOpen); setStatusOpen(false); }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                  <span className="rbtxt">
                    {dateRange.from && dateRange.to ? `${dateRange.from} → ${dateRange.to}` : t("Teams_dashboard.filter.SetDate")}
                  </span>
                </button>

                <div className="range-popover">
                  <h4>{t("Teams_dashboard.filter.SelectTimeRange")}</h4>
                  <div className="quick-ranges">
                    <button onClick={() => {
                      const d = new Date().toISOString().split('T')[0];
                      setDateRange({ from: d, to: d });
                    }}>{t("Teams_dashboard.filter.Today")}</button>
                    <button onClick={() => {
                      const d = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0];
                      setDateRange({ from: d, to: d });
                    }}>{t("Teams_dashboard.filter.Yesterday")}</button>
                    {[7, 14, 30].map(d => (
                      <button key={d} onClick={() => {
                        const to = new Date().toISOString().split('T')[0];
                        const from = new Date(Date.now() - d * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
                        setDateRange({ from, to });
                      }}>
                        {d} {t("Teams_dashboard.filter.DaysAgo")}
                      </button>
                    ))}
                    <button
                      onClick={() => {
                        setDateRange({ from: "", to: "" });
                        setPeriod("all");
                        setTableStartDate("all");
                        setTableEndDate("");
                      }}>
                      {t("Teams_dashboard.filter.All")}
                    </button>
                  </div>
                  <div className="range-fields">
                    <div className="field">
                      <label>{t("Teams_dashboard.filter.FromDate")}</label>
                      <input type="date" value={dateRange.from} onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })} />
                    </div>
                    <span className="arrow-sep">→</span>
                    <div className="field">
                      <label>{t("Teams_dashboard.filter.ToDate")}</label>
                      <input type="date" value={dateRange.to} onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })} />
                    </div>
                  </div>
                  <div className="popover-actions">
                    <button className="btn-ghost" onClick={() => setRangeOpen(false)}>{t("Teams_dashboard.filter.Cancel")}</button>
                    <button className="btn-primary" onClick={() => {
                      if (dateRange.from && dateRange.to) {
                        setPeriod("custom");
                        setTableStartDate(dateRange.from);
                        setTableEndDate(dateRange.to);
                      }
                      setRangeOpen(false);
                    }}>{t("Teams_dashboard.filter.Apply")}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <button
            type="button"
            className="tdv2-export-btn"
            onClick={exportMembersCsv}
            disabled={filteredMembers.length === 0}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
            <span className="txt">{t("ExportCsv")}</span>
          </button>
        </div>
        <div className={`tdv2-tbl-wrap${tableLoading ? " is-loading" : ""}`}>
          {tableLoading && (
            <div className="tdv2-tbl-loading">
              <Spin />
            </div>
          )}
          <table className="tdv2-table">
            <thead>
              <tr>
                <th>{t("Member")}</th>
                <th>{t("Stage")}</th>
                {/* <th>{t("Teams_dashboard.Pnl")}</th> */}
                <th>{t("Teams_dashboard.Drawdown")}</th>
                <th>{t("Teams_dashboard.Roadmap")}</th>
                <th>{t("NoStopLoss")}</th>
                <th>{t("Teams_dashboard.Journal")}</th>
                <th>{t("AccountHealth")}</th>
                <th>{t("Warnings")}</th>
              </tr>
            </thead>
            <tbody>
              {filteredMembers.length === 0 && !tableLoading && (
                <tr>
                  <td colSpan={8} className="tdv2-td-empty">{t("NoMatchingMembers")}</td>
                </tr>
              )}
              {pagedMembers.map((m) => (
                <MemberRow key={m.account_number} m={m} t={t} failed={failedAccounts.has(m.account_number)} />
              ))}
            </tbody>
          </table>
        </div>
        {filteredMembers.length > 0 && (
          <div className="tdv2-table-foot">
            <span className="range">
              {t("ShowingRange", {
                from: (tablePage - 1) * tablePageSize + 1,
                to: Math.min(tablePage * tablePageSize, filteredMembers.length),
                total: filteredMembers.length,
              })}
            </span>
            <Pagination
              size="small"
              current={tablePage}
              pageSize={tablePageSize}
              total={filteredMembers.length}
              showSizeChanger
              pageSizeOptions={[10, 20, 50]}
              locale={{ items_per_page: t("PerPage") }}
              onChange={(p, size) => {
                setTablePage(size !== tablePageSize ? 1 : p);
                setTablePageSize(size);
              }}
            />
          </div>
        )}
      </div>

      {/* NOTIFICATIONS MODAL */}
      <Modal title={t("AllNotifications")} open={notifOpen} onCancel={() => setNotifOpen(false)} footer={null} width={620} wrapClassName="tdv2-modal-wrap">
        <div className="tdv2-modal">
          <div className="tdv2-modal-list">
            {renderQueueFilters()}
            {renderSupportList(false)}
          </div>
        </div>
      </Modal>
    </div>
  );
};

const MemberRow: React.FC<{ m: DashboardMember; t: (k: string) => string; failed?: boolean }> = ({ m, t, failed }) => {
  const navigate = useNavigate()
  const isDanger = !failed && isDangerMember(m);
  const stage = STAGE_MAP[m.stage];
  const [avatarBroken, setAvatarBroken] = useState(false);

  // health
  let health: { color: string; label: string };
  if (m.healthStatus) {
    const h = m.healthStatus.toLowerCase();
    if (h.includes("nguy hiểm") || h.includes("danger") || h.includes("red")) health = { color: "#E24B4A", label: m.healthStatus };
    else if (h.includes("cần chú ý") || h.includes("attention") || h.includes("amber") || h.includes("warning")) health = { color: "#EF9F27", label: m.healthStatus };
    else if (h.includes("phát triển") || h.includes("growing") || h.includes("blue")) health = { color: "#22C55E", label: m.healthStatus };
    else health = { color: "#4CAF50", label: m.healthStatus };
  } else {
    if (m.dd >= 7) health = { color: "#E24B4A", label: t("DangerHealth") };
    else if (m.dd >= 4 || m.sl >= 3) health = { color: "#EF9F27", label: t("NeedAttention") };
    else if (m.pnl >= 5) health = { color: "#22C55E", label: t("GrowingHealth") };
    else health = { color: "#4CAF50", label: t("Stable") };
  }

  if (failed) health = { color: "#9B9589", label: t("SupportLevel.failed") };
  // dd bar
  const ddPct = Math.min((m.dd / 10) * 100, 100);
  const ddCol = m.dd >= 7 ? "#E24B4A" : m.dd >= 4 ? "#EF9F27" : "#5C5849";

  // flags
  const flags: { cls: string; label: string }[] = [];
  if (m.alerts && m.alerts.length > 0) {
    m.alerts.forEach((alert) => {
      let cls = "a";
      const al = alert.toLowerCase();
      if (al.includes("vi phạm") || al.includes("danger") || al.includes("nguy hiểm") || al.includes("stop loss")) cls = "r";
      else if (al.includes("pass") || al.includes("tốt")) cls = "g";
      else if (al.includes("giảm") || al.includes("vốn")) cls = "b";
      flags.push({ cls, label: alert });
    });
  } else {
    if (m.sl >= 5) flags.push({ cls: "r", label: t("SLViolated") });
    else if (m.sl >= 2) flags.push({ cls: "a", label: t("SLAttention") });
    if (m.journal >= 4) flags.push({ cls: "r", label: t("MissingJournal") });
    else if (m.journal >= 1) flags.push({ cls: "a", label: t("JournalShort") });
    if (m.dd >= 7) flags.push({ cls: "r", label: t("NearMaxLoss") });
    else if (m.dd >= 4) flags.push({ cls: "a", label: t("HighDrawdown") });
    if (m.pnl >= 7.5) flags.push({ cls: "b", label: t("ReduceRisk") });
    if (m.pnl >= 4 && m.stage === "verification") flags.push({ cls: "g", label: t("AboutToPass") });
  }

  const slColor = m.sl === 0 ? "var(--green)" : m.sl <= 2 ? "var(--amber)" : "var(--red)";

  return (
    <tr className={failed ? "failed" : isDanger ? "danger" : ""}>
      <td className="tdv2-td-member" onClick={() => {
        navigate(`/${m.platform}/account-live-metrix/${m.account_number}`)
      }}>
        <div className="tdv2-name-cell cursor-pointer">
          {
            m.avatar_url && !avatarBroken ? (
              <img src={m.avatar_url} alt={m.name} className="tdv2-avatar object-cover" onError={() => setAvatarBroken(true)} />
            ) : (
              <div className="tdv2-avatar" style={{ background: m.avBg, color: m.avColor }}>{(m.shortName || m.name).charAt(0).toUpperCase()}</div>
            )

          }
          <div>
            <div className="tdv2-nm">{m.name}</div>
            <div className="tdv2-acc-size">{m.account_number}</div>
          </div>
        </div>
      </td>
      <td className="tdv2-td-stage" data-label={t("Stage")}>
        <span className="tdv2-stage-pill" style={{ background: stage.bg, color: stage.c }}>
          {m.stage === 'funded' ? t("Teams_dashboard.filter.Funded") :
            m.stage === 'challenge' ? t("Teams_dashboard.filter.Challenge") :
              m.stage === 'verification' ? t("Teams_dashboard.filter.Verification") :
                m.stage === 'new' ? t("New") : stage.label}
        </span>
      </td>
      {/* <td>
        <span className={`tdv2-pnl ${m.pnl >= 0 ? "pos" : "neg"}`}>{m.pnl >= 0 ? "+" : ""}{m.pnl}%</span>
      </td> */}
      <td data-label={t("Teams_dashboard.Drawdown")}>
        <div className="tdv2-dd-cell">
          <div className="tdv2-dd-bar"><div className="tdv2-dd-fill" style={{ width: `${ddPct}%`, background: ddCol }} /></div>
          <span style={{ fontSize: 12, color: ddCol }}>{m.dd}%</span>
        </div>
      </td>
      <td data-label={t("Teams_dashboard.Roadmap")}>
        {m.road > m.roadTarget ? (
          <div className="tdv2-road-cell">
            <div style={{ fontSize: 11, color: "var(--red)", fontWeight: 600 }}>{t("OutOfRoadmap")}</div>
          </div>
        ) : (
          <div className="tdv2-road-cell">
            <div className="tdv2-road-bar">
              <div
                className="tdv2-road-fill"
                style={{
                  width: `${Math.min((Math.max(0, m.road) / m.roadTarget) * 100, 100)}%`,
                  // Giữ hue xanh lá cố định (130), chỉ đậm dần theo % tiến độ
                  // 0%  -> HSL(130, 35%, 68%) xanh lá nhạt
                  // 100% -> HSL(130, 55%, 28%) xanh lá đậm
                  backgroundColor: (() => {
                    const pct = Math.min((Math.max(0, m.road) / m.roadTarget) * 100, 100);
                    const sat = 35 + pct * 0.20;   // 35% -> 55%
                    const light = 68 - pct * 0.40; // 68% -> 28%
                    return `hsl(130, ${sat}%, ${light}%)`;
                  })()
                }}
              />
            </div>
            <div className="tdv2-road-txt">{m.road >= 0 ? "+" : ""}{m.road}% → {m.roadTarget}%</div>
          </div>
        )}
      </td>
      <td data-label={t("NoStopLoss")}>
        <span className="tdv2-sl" style={{ color: slColor }}>{m.sl} {t("Times")}</span>
      </td>
      <td data-label={t("Teams_dashboard.Journal")}>
        {m.journal === 0 ? (
          <span className="tdv2-sl" style={{ color: "var(--green)" }}>✓ {t("Enough")}</span>
        ) : (
          <span className="tdv2-sl" style={{ color: "var(--amber)" }}>{t("Missing")} {m.journal}</span>
        )}
      </td>
      <td className="tdv2-td-health" data-label={t("AccountHealth")}>
        <div className="tdv2-health"><div className="tdv2-hdot" style={{ background: health.color }} /><span style={{ color: health.color }}>{health.label}</span></div>
      </td>
      <FlagCell flags={flags} label={t("Warnings")} />
    </tr>
  );
};

function FlagCell({ flags, label }: { flags: Array<{ cls: string; label: string }>; label?: string }) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!flags || flags.length === 0) {
    return (
      <td className="tdv2-td-flags" data-label={label}>
        <div className="tdv2-flags">
          <span style={{ fontSize: 11, color: "var(--text-3)" }}>—</span>
        </div>
      </td>
    );
  }

  return (
    <td className="tdv2-td-flags" data-label={label}>
      <div className="tdv2-flags">
        {isExpanded || flags.length === 1 ? (
          flags.map((f, i) => (
            <span key={i} className={`tdv2-flag ${f.cls}`} onClick={(e) => { e.stopPropagation(); setIsExpanded(false); }} style={{ cursor: 'pointer' }}>
              {f.label}
            </span>
          ))
        ) : (
          <>
            <span className={`tdv2-flag ${flags[0].cls}`}>{flags[0].label}</span>
            <span
              className="tdv2-flag flag-more"
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(true)
              }}
              style={{ cursor: 'pointer' }}
            >
              +{flags.length - 1}
            </span>
          </>
        )}
      </div>
    </td>
  );
}

export default TeamDashboardTabV2;
